import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { catchError, firstValueFrom, of } from "rxjs";
import { ApiService } from "../core/api.service";
import { Order, OrderItem, OrderStatus, StripePaymentRecord } from "../core/models";
import {
  SupplierStage,
  supplierStage,
  stageLabel,
  supplierSteps,
  supplierScope,
  sortSupplierIds,
} from "./supplier.util";

const STATUS_FLOW: OrderStatus[] = [
  "submitted",
  "confirmed",
  "in_production",
  "ready",
  "delivered",
];

const STATUS_LABEL: Record<OrderStatus, string> = {
  submitted: "Submitted",
  confirmed: "Confirmed",
  in_production: "In production",
  ready: "Ready for delivery",
  delivered: "Delivered",
};

interface AddressDraft {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
}

interface Draft {
  customerName: string;
  customerEmail: string;
  address: AddressDraft;
  items: OrderItem[];
  tax: number;
  trackingNumber: string;
}

const BLANK_ITEM: OrderItem = {
  blindId: "",
  blindCode: "",
  blindName: "",
  label: "Window",
  roomName: "",
  controlType: "cordless",
  opacity: "semi-blackout",
  width: 30,
  height: 60,
  price: 0,
  hasSolarPanel: false,
  isRemote: false,
};

@Component({
  selector: "app-order-detail-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: "./order-detail-page.component.html",
  styleUrls: ["./order-detail-page.component.scss"],
})
export class OrderDetailPageComponent {
  private readonly api = inject(ApiService);
  private readonly id = inject(ActivatedRoute).snapshot.paramMap.get("id") ?? "";

  readonly statusFlow = STATUS_FLOW;
  readonly busy = signal(false);
  readonly saved = signal(false);
  readonly editing = signal(false);
  readonly activeTab = signal<"actions" | "measurements">("actions");
  readonly notice = signal<{ kind: "success" | "error"; text: string } | null>(
    null,
  );
  readonly loading = signal(true);
  readonly order = signal<Order | null>(null);
  readonly draft = signal<Draft | null>(null);

  readonly supplierList = toSignal(
    this.api
      .listSuppliers()
      .pipe(
        catchError(() =>
          of(
            [] as {
              id: string;
              name: string;
              email: string;
              createdAt: string;
            }[],
          ),
        ),
      ),
    {
      initialValue: [] as {
        id: string;
        name: string;
        email: string;
        createdAt: string;
      }[],
    },
  );

  /** Suppliers sorted action-first for the card list. */
  readonly suppliers = computed(() => {
    const o = this.order();
    if (!o) return [] as string[];
    return sortSupplierIds(o.supplierIds ?? [], o.supplierQuoteStatuses ?? {});
  });

  constructor() {
    void this.load();
  }

  private async load() {
    this.loading.set(true);
    try {
      const o = await firstValueFrom(this.api.getOrder(this.id));
      this.order.set(o);
      this.draft.set(this.makeDraft(o));
    } catch {
      this.order.set(null);
    } finally {
      this.loading.set(false);
    }
  }

  private makeDraft(o: Order): Draft {
    const a = o.shippingAddress ?? ({} as Partial<AddressDraft>);
    return {
      customerName: o.customerName ?? "",
      customerEmail: o.customerEmail ?? "",
      address: {
        name: a.name ?? "",
        line1: a.line1 ?? "",
        line2: a.line2 ?? "",
        city: a.city ?? "",
        state: a.state ?? "",
        zip: a.zip ?? "",
        phone: a.phone ?? "",
      },
      items: o.items.map((it) => ({ ...it })),
      tax: o.tax ?? 0,
      trackingNumber: o.trackingNumber ?? "",
    };
  }

  startEdit() {
    this.saved.set(false);
    this.editing.set(true);
  }

  setTab(tab: "actions" | "measurements") {
    this.activeTab.set(tab);
  }

  cancelEdit() {
    this.editing.set(false);
    this.saved.set(false);
    const o = this.order();
    if (o) this.draft.set(this.makeDraft(o));
  }

  subtotal(): number {
    return (this.draft()?.items ?? []).reduce(
      (sum, it) => sum + (Number(it.price) || 0),
      0,
    );
  }

  total(): number {
    return Math.round((this.subtotal() + (this.draft()?.tax ?? 0)) * 100) / 100;
  }

  addItem() {
    const d = this.draft();
    if (!d) return;
    this.draft.set({ ...d, items: [...d.items, { ...BLANK_ITEM }] });
  }

  removeItem(index: number) {
    const d = this.draft();
    if (!d) return;
    this.draft.set({ ...d, items: d.items.filter((_, i) => i !== index) });
  }

  private async run(action: () => Promise<void>, successText?: string) {
    if (this.busy()) return;
    this.busy.set(true);
    this.notice.set(null);
    try {
      await action();
      if (successText) this.notice.set({ kind: "success", text: successText });
    } catch {
      this.notice.set({
        kind: "error",
        text: "Something went wrong. Please try again.",
      });
    } finally {
      this.busy.set(false);
    }
  }

  save() {
    const d = this.draft();
    if (!d) return;
    return this.run(async () => {
      const updated = await firstValueFrom(
        this.api.updateOrder(this.id, {
          customerName: d.customerName,
          customerEmail: d.customerEmail,
          shippingAddress: d.address,
          items: d.items,
          tax: Number(d.tax) || 0,
          trackingNumber: d.trackingNumber,
        }),
      );
      this.order.set(updated);
      this.draft.set(this.makeDraft(updated));
      this.editing.set(false);
      this.saved.set(true);
    });
  }

  setStatus(status: OrderStatus) {
    return this.run(async () => {
      const updated = await firstValueFrom(
        this.api.updateOrderStatus(this.id, status),
      );
      this.order.set(updated);
    }, "Status updated.");
  }

  markPaid(supplierId: string) {
    return this.run(async () => {
      await firstValueFrom(this.api.markDiyPaid(this.id, supplierId));
      await this.load();
    }, "Marked paid — supplier notified to start production.");
  }

  approveQuote(supplierId: string) {
    return this.run(async () => {
      await firstValueFrom(this.api.approveDiyQuote(this.id, supplierId));
      await this.load();
    }, "Quote approved.");
  }

  rejectQuote(supplierId: string) {
    return this.run(async () => {
      await firstValueFrom(this.api.rejectDiyQuote(this.id, supplierId));
      await this.load();
    }, "Quote rejected.");
  }

  supplierName(supplierId: string): string {
    return (
      this.supplierList().find((s) => s.id === supplierId)?.name ??
      "Unknown supplier"
    );
  }

  supplierEmail(supplierId: string): string {
    return this.supplierList().find((s) => s.id === supplierId)?.email ?? "";
  }

  readonly stageLabel = stageLabel;

  stageFor(supplierId: string): SupplierStage {
    return supplierStage(this.order()?.supplierQuoteStatuses?.[supplierId]);
  }

  stepsFor(supplierId: string) {
    return supplierSteps(
      this.order()?.supplierQuoteStatuses?.[supplierId],
      !!this.alibabaUrl(supplierId),
      !!this.order()?.trackingNumber,
    );
  }

  scopeText(supplierId: string): string {
    const o = this.order();
    if (!o) return "";
    const s = supplierScope(o.items, supplierId);
    const rooms = s.rooms.join(" · ");
    const bits: string[] = [];
    if (s.windowCount) {
      bits.push(`${s.windowCount} window${s.windowCount === 1 ? "" : "s"}`);
    }
    if (s.remoteCount) {
      bits.push(`${s.remoteCount} remote${s.remoteCount === 1 ? "" : "s"}`);
    }
    const detail = bits.join(" + ");
    if (rooms && detail) return `${rooms} — ${detail}`;
    return rooms || detail;
  }

  quoteFor(supplierId: string) {
    return this.order()?.supplierQuotes?.[supplierId] ?? null;
  }

  quoteUrl(supplierId: string): string | null {
    return this.quoteFor(supplierId)?.url ?? null;
  }

  alibabaUrl(supplierId: string): string | null {
    return this.order()?.supplierAlibabaUrls?.[supplierId] ?? null;
  }

  readonly orderTracking = computed(
    () => this.order()?.trackingNumber?.trim() ?? "",
  );

  trackUrl(): string | null {
    const tracking = this.orderTracking();
    return tracking
      ? `https://www.aftership.com/track?t=${encodeURIComponent(tracking)}&c=sf-express`
      : null;
  }

  statusLabel(status: OrderStatus): string {
    return STATUS_LABEL[status] ?? status;
  }

  /** Step state for the order-status stepper (done / current / upcoming). */
  statusStepState(index: number): "done" | "current" | "todo" {
    const current = this.statusFlow.indexOf(this.order()?.status ?? "submitted");
    if (current < 0) return "todo";
    if (index < current) return "done";
    if (index === current) return "current";
    return "todo";
  }

  money(value: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value ?? 0);
  }

  stripeUrl(payment: StripePaymentRecord): string {
    const base = payment.livemode
      ? "https://dashboard.stripe.com/payments"
      : "https://dashboard.stripe.com/test/payments";
    return `${base}/${payment.paymentIntentId}`;
  }
}
